import { useEffect, useRef, useState } from 'react';
import { api } from './api';

const ENDPOINTS = {
  crew: 'projects/api/availability/crew/',
  vehicle: 'projects/api/availability/vehicles/',
  stock: 'projects/api/availability/stock/',
} as const;

type ConflictKind = keyof typeof ENDPOINTS;

export interface ConflictProject {
  id: number;
  name: string;
  date_from?: string | null;
  date_to?: string | null;
  [key: string]: unknown;
}

export interface ConflictCheckResult {
  count: number;
  conflicts?: ConflictProject[];
  conflicting_projects?: ConflictProject[];
  shortage?: number;
  [key: string]: unknown;
}

/**
 * Debounced, on-demand double-booking / overlap check against one of the
 * projects/api/availability/* endpoints. Returns null while disabled, while
 * required params are missing, or before the first response arrives.
 */
export function useConflictCheck(
  kind: ConflictKind,
  params: Record<string, string | number | null | undefined>,
  enabled = true
): ConflictCheckResult | null {
  const [result, setResult] = useState<ConflictCheckResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !params.date_from || !params.date_to) {
      setResult(null);
      return undefined;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          query.set(key, String(value));
        }
      });
      api
        .get<ConflictCheckResult>(`${ENDPOINTS[kind]}?${query.toString()}`)
        .then(setResult)
        .catch(() => setResult(null));
    }, 400);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, enabled, JSON.stringify(params)]);

  return result;
}
